// @flow
import * as React from 'react';
import classNames from 'classnames';
import _ from 'lodash';

import SelectOptions from './SelectOptions';
import * as css from './Select.scss';

type Props = {
	index?: number,
	className?: string,
	defaultLabel?: string,
	defaultOptionIndex?: number,
	isActive?: boolean,
	options: Array<string>,
	onChange: (index: number, city: string) => void,
	clearable?: boolean,
	labelPostfix?: string,
	searchable?: boolean,
};

const withPostfix = (value, props) => {
	const v = value.replace(/\s\s+/g, ' ');
	return `${v}${v === props.defaultLabel ? '' : props.labelPostfix}`;
};

const onButtonKeyUp = e => {
	if (e.keyCode === 32) {
		e.preventDefault();
	}
};

const Select = (props: Props) => {
	const containerRef = React.useRef<null | HTMLElement>(null);
	const inputRef = React.useRef<null | HTMLElement>(null);

	const { className, options, defaultLabel, defaultOptionIndex, index, isActive, onChange } = props;
	const [isOpen, setIsOpen] = React.useState<boolean>(false);
	const [currentOption, setCurrentOption] = React.useState<string>(_.get(options, defaultOptionIndex, ''));

	const [searchableValue, setSearchableValue] = React.useState(withPostfix(currentOption, props));
	const [isTyping, setTyping] = React.useState(false);

	const onClick = React.useCallback(
		event => {
			if (isOpen && event.target === inputRef.current) {
				return; // prevent close on input click
			}

			setIsOpen(prevIsOpen => {
				const nextIsOpen = !prevIsOpen;
				if (nextIsOpen && props.searchable) {
					inputRef.current.select();
				} else {
					setTyping(false);
				}
				return nextIsOpen;
			});
		},
		[isOpen, props.searchable]
	);

	const canClear = currentOption && props.clearable;
	const onIconClick = React.useCallback(
		event => {
			if (canClear) {
				event.stopPropagation();
				setCurrentOption('');
				onChange(-1, currentOption);
			}
		},
		[canClear]
	);

	const onOptionClick = React.useCallback(option => {
		setCurrentOption(option);
		onChange(index || 0, option);
		setTyping(false);
		setIsOpen(false);
	}, []);

	const onInputChange = React.useCallback(
		e => {
			if (!isTyping) setTyping(true);
			setSearchableValue(e.target.value);
		},
		[isTyping]
	);

	// Handle click outside
	const onDocumentClick = React.useCallback((e: MouseEvent) => {
		const target: HTMLElement = (e.target: any);

		if (containerRef.current && containerRef.current.contains(target)) {
			return;
		}

		setIsOpen(false);
	}, []);

	React.useEffect(() => {
		document.addEventListener('click', onDocumentClick);

		if (defaultOptionIndex !== undefined && options[defaultOptionIndex] !== undefined && defaultOptionIndex >= 0) {
			onChange(defaultOptionIndex, options[defaultOptionIndex]);
		}

		return () => {
			document.removeEventListener('click', onDocumentClick);
		};
	}, []);

	// Make sure that searchable value is always equal to currentOption
	React.useEffect(() => {
		if (!isOpen) {
			setSearchableValue(_.trim(withPostfix(currentOption, props)));
		}
	}, [isOpen, currentOption]);

	// Options
	const filterBySelected = o => o !== currentOption;
	const filterByTyping = o => filterBySelected(o) && withPostfix(o, props).indexOf(searchableValue) > -1;
	const availableOptions = _.filter(options, isTyping && isOpen ? filterByTyping : filterBySelected);

	return (
		<div
			className={classNames(
				css.select,
				isOpen && css.open,
				isActive && css.active,
				{ [css.selected]: currentOption },
				{ [css.clearable]: props.clearable },
				className
			)}
			ref={containerRef}
		>
			<button className={css.button} onClick={onClick} type="button" onKeyUp={onButtonKeyUp}>
				<div className={css.label}>
					<input
						ref={inputRef}
						type="text"
						value={searchableValue}
						onChange={onInputChange}
						readOnly={!props.searchable}
						placeholder={defaultLabel}
					/>
				</div>
				<div className={css.icon} onClick={onIconClick} />
			</button>
			<SelectOptions
				isShown={isOpen}
				options={availableOptions}
				onOptionClick={onOptionClick}
				labelPostfix={props.labelPostfix}
			/>
		</div>
	);
};

Select.defaultProps = {
	index: 0,
	className: '',
	defaultLabel: 'Select',
	defaultOptionIndex: -1,
	isActive: true,
	clearable: false,
	labelPostfix: '',
	searchable: true,
};

export default Select;
